/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	languagesOptions,
	languageUsageForGame,
	presetUsageForGame,
	UserRole,
	type Game,
	type GameId,
	type GameLicense,
} from 'shared/modelTypes/index';
import { Archive, ArchiveRestore, CheckCheck, Download, FileWarning, Pen, Trash } from 'lucide-static';
import { Icon } from './Icon';
import { Button } from './Button';
import { Avatar } from './Avatar';
import { InlineCode } from './InlineCode';
import QRCode from 'qrcode';
import { idToQr, lateinit, toTitleCase } from 'shared/util';
import { rpc } from 'src/backend';
import { navigate, reloadCurrentView, type Path } from 'engine/router';
import { Modal, showModal } from './Modal';
import { ArrayField, DropdownField, FileField, Form, TextField } from './FormComponents';
import { medalForEmotions, ZintuigenEmotions, ZintuigenPresetSchema, type ZintuigenPreset } from 'shared/modelTypes/facilitaire-zintuigen';
import { showToast, Toast } from './Toast';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { JackpotPresetSchema, type JackpotCard, type JackpotPreset } from 'shared/modelTypes/jackpot';
import { defaultRoundWordData } from 'shared/jackpotDefaults';
import { IconPicker } from './jackpot/IconPicker';
import { render, type JSXChild } from 'engine/index';
import {
	defaultQuestionsData,
	VingerAfdrukkenPresetSchema,
	type VingerAfdrukkenPreset,
	type VingerAfdrukZone,
} from 'shared/modelTypes/vinger-afdrukken';
import { BouwHetLandschapPresetSchema, type BouwHetLandschapPreset } from 'shared/modelTypes/bouw-het-landschap';
import { PlayAndConnectPresetSchema, type PlayAndConnectPreset } from 'shared/modelTypes/play-and-connect';

type GameLicenseViewProps = {
	license: GameLicense;
	game: Game;
	role: UserRole;
};

export class GameLicenseView extends Base.Component<GameLicenseViewProps> {
	showArchived: boolean = false;
	sessionsContainer: HTMLElement = lateinit;

	override render(): HTMLElement {
		const { game, license, role } = this.props;
		const presetUsage = presetUsageForGame(game.kind);
		const languageUsage = languageUsageForGame(game.kind);
		const availableLanguagesOptions = languagesOptions.filter((l) => languageUsage.includes(l.value));

		let language = languageUsage[0];
		let presetIndex = 0;

		const archivedCount = license.playSessions.filter((s) => s.archivedAt).length;

		const view: HTMLElement = (
			<div class="mx-auto w-fit max-w-full rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800">
				<div class="mb-4 flex items-center justify-between">
					<div class="flex items-center gap-4">
						<Avatar src={`/images/${game.kind}.jpg`} alt="game icon" />
						<h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
							{game.name ?? game.kind} license
							{license.name ? ' - ' + license.name : ''}
						</h2>
					</div>
				</div>
				Code: <InlineCode clipboardButton>{idToQr(license.code ? license.code : parseInt(license._key!))}</InlineCode>
				<img qr />
				{presetUsage !== 'no-preset' && <PresetsView license={license} />}
				{(license.playSessions.at(-1) === undefined || license.playSessions.at(-1)?.to !== undefined) && (
					<div>
						<span class="text-gray-700">Geen active speelSessie.</span>
						<div class="flex gap-3">
							{presetUsage !== 'no-preset' && (
								<DropdownField
									compact
									label="Preset"
									name="preset"
									onChange={(v) => {
										if (v) presetIndex = v;
									}}
									options={(license.presets as ZintuigenPreset[]).map((p, i) => ({ value: i, label: p.presetName }))}
								/>
							)}
							{availableLanguagesOptions.length > 1 && (
								<DropdownField
									compact
									label="Taal"
									name="taal"
									onChange={(l) => {
										if (l) language = l;
									}}
									options={availableLanguagesOptions.map((lang) => ({ value: lang.value, label: lang.label }))}
								/>
							)}
							<Button
								on:click={async () => {
									const preset = license.presets[presetIndex];
									const _state = await rpc.startOrResumeGame.query({ sessionId: license._key!, preset, language });
									navigate(`/dashboard/licenses/play/${license._key}`);
								}}
							>
								Start spel
							</Button>
						</div>
					</div>
				)}
				<div class="h-4"></div>
				<h3 class="text-lg font-bold text-gray-900">Speelsessies</h3>
				{role === 'admin' && archivedCount > 0 && (
					<label class="flex items-center gap-2 py-2 text-sm text-gray-700 dark:text-gray-300">
						<input
							type="checkbox"
							on:change={(e: Event) => {
								this.showArchived = (e.target as HTMLInputElement).checked;
								this.renderSessions();
							}}
						/>
						<span>Toon gearchiveerd ({archivedCount})</span>
					</label>
				)}
				<div sessions-container></div>
				{role === 'admin' && (
					<>
						<hr class="my-3 dark:border-gray-700" />
						<div class="flex gap-4">
							<Button mode="secondary" href={'/dashboard/licenses/edit/' + license._key}>
								Edit
								<Icon icon={Pen} class="size-3" />
							</Button>
						</div>
					</>
				)}
			</div>
		);

		this.sessionsContainer = view.querySelector('[sessions-container]') as HTMLElement;
		this.renderSessions();

		const qrContainer = view.querySelector('[qr]') as HTMLImageElement;
		QRCode.toDataURL(import.meta.env.VITE_QRCODE_URL + idToQr(license.code ? license.code : parseInt(license._key!))).then(
			(qr) => (qrContainer.src = qr),
		);

		return view;
	}

	renderSessions() {
		const { license, role } = this.props;
		const sessions = license.playSessions.slice().reverse();
		const fmt = (iso: string, dateOnly = false) =>
			dateOnly ? new Date(iso).toLocaleDateString('sv-SE') : new Date(iso).toLocaleString('sv-SE').slice(0, 16);

		const nodes = sessions
			.map((session) => {
				const isArchived = !!session.archivedAt;

				if (isArchived && !(role === 'admin' && this.showArchived)) {
					return null;
				}

				const dimClass = isArchived ? 'opacity-60' : '';

				return (
					<div class={dimClass}>
						{session.to && (
							<div class="flex items-center gap-1">
								<span class="min-w-[17rem] tabular-nums">
									{fmt(session.from)} - {session.to && fmt(session.to)}
								</span>
								<Button
									on:click={async () => {
										const res = await rpc.downloadReport.query({ id: license._key!, sessionUuid: session.uuid });
										if (!res.success) {
											showToast(<Toast icon={FileWarning} title={res.error} />, 3_000);
										} else {
											const path = res.reportUrl;
											const url = new URL(path, window.location.origin);
											const link = document.createElement('a');
											link.href = url.toString();
											link.download = path.split('/').pop() || 'download';
											document.body.appendChild(link);
											link.click();
											document.body.removeChild(link);
										}
									}}
									style="display:inline"
									mode="tertiary"
								>
									<Icon icon={Download} class="size-4" />
								</Button>
								{!isArchived && (
									<Button
										on:click={async () => {
											const confirmed = window.confirm(
												'Weet je zeker dat je deze rapportage wilt archiveren? Deze verdwijnt uit je overzicht.',
											);
											if (!confirmed) return;
											const res = await rpc.archivePlaySession.query({
												licenseId: license._id!,
												sessionUuid: session.uuid,
											});
											if (!res.success) {
												showToast(<Toast icon={FileWarning} title={res.error} />, 3_000);
											} else {
												showToast(<Toast icon={Archive} title="Rapportage gearchiveerd" />, 3_000);
												reloadCurrentView();
											}
										}}
										style="display:inline"
										mode="tertiary"
									>
										<Icon icon={Archive} class="size-4" />
									</Button>
								)}
								{isArchived && role === 'admin' && (
									<Button
										title={`gearchiveerd op ${session.archivedAt && fmt(session.archivedAt, true)}`}
										on:click={async () => {
											const res = await rpc.unarchivePlaySession.query({
												licenseId: license._id!,
												sessionUuid: session.uuid,
											});
											if (!res.success) {
												showToast(<Toast icon={FileWarning} title={res.error} />, 3_000);
											} else {
												showToast(<Toast icon={ArchiveRestore} title="Rapportage teruggezet" />, 3_000);
												reloadCurrentView();
											}
										}}
										style="display:inline"
										mode="tertiary"
									>
										<Icon icon={ArchiveRestore} class="size-4" />
									</Button>
								)}
							</div>
						)}
						{!session.to && (
							<div class="flex gap-3">
								<span>{fmt(session.from)} - huidig</span>
								<Button href={`/dashboard/licenses/play/${license._key}`}>Doorspelen</Button>
								<Button
									mode="secondary"
									on:click={async () => {
										await rpc.endPlaySession.query({ licenseId: license._id! });
										navigate(window.location.pathname as Path);
									}}
								>
									Stoppen
								</Button>
							</div>
						)}
					</div>
				);
			})
			.filter((n): n is HTMLElement => n !== null);

		if (!nodes.length) {
			render(this.sessionsContainer, [<span class="text-gray-700">Nog niet gespeeld.</span>]);
		} else {
			render(this.sessionsContainer, nodes);
		}
	}
}

function PresetsView({ license }: { license: GameLicense }) {
	function colsForPreset(kind: GameId): Record<string, (d: any) => JSXChild> {
		switch (kind) {
			case 'team-kleuren-spel':
				return {};
			case 'kwaliteiten-jacht':
				return {};
			case 'facilitaire-zintuigen-spel':
				return { Steps: (game: ZintuigenPreset) => game.rounds.map((step) => step.topic).join(', ') };
			case 'jackpot-van-las-vegas':
				return {
					'Ronde 1 titel': (p: JackpotPreset) => p.round1Title,
					'Ronde 2 titel': (p: JackpotPreset) => p.round2Title,
					'Ronde 3 titel': (p: JackpotPreset) => p.round3Title,
				};
			case 'vinger-afdrukken-spel':
				return {
					'Thema 1': (game: VingerAfdrukkenPreset) => game.theme1.name,
					'Thema 2': (game: VingerAfdrukkenPreset) => game.theme2.name,
					'Thema 3': (game: VingerAfdrukkenPreset) => game.theme3.name,
					'Thema 4': (game: VingerAfdrukkenPreset) => game.theme4.name,
				};
			case 'bouw-het-landschap-spel':
				return {
					'Thema 1': (game: BouwHetLandschapPreset) => game.theme1.name,
					'Thema 2': (game: BouwHetLandschapPreset) => game.theme2.name,
					'Thema 3': (game: BouwHetLandschapPreset) => game.theme3.name,
					'Thema 4': (game: BouwHetLandschapPreset) => game.theme4.name,
				};
			case 'flikken-challenge':
				return {};
			case 'reflectora-spel':
				return {};
			case 'play-and-connect-spel':
				return {
					Vragen: (game: PlayAndConnectPreset) => game.rounds.map((step) => step.question).join(', '),
					Rondes: (game: PlayAndConnectPreset) => game.rounds.map((step) => step.rounds ?? 1).join(', '),
				};
		}
	}

	return (
		<div class="py-2">
			<details open class="overflow-hidden rounded-md border border-gray-200">
				<summary class="select-none border-b border-gray-100 bg-gray-50 p-2">
					<h3 class="inline-block font-bold text-gray-800">Presets</h3>
				</summary>
				<div class="p-2">
					<Button
						on:click={() => {
							presetEditModal(license);
						}}
					>
						New preset
					</Button>
					{license.presets && <PresetTable license={license} cols={colsForPreset(license.kind)} />}
				</div>
			</details>
		</div>
	);
}

function FacilitaireZintuigenPresetForm({
	data,
	onSubmit,
	onCancel,
}: {
	data?: ZintuigenPreset;
	onSubmit: (d: ZintuigenPreset) => void;
	onCancel: () => void;
}) {
	if (!data) data = ZintuigenPresetSchema.parse({});
	return (
		<Form schema={ZintuigenPresetSchema} onSubmit={onSubmit}>
			<TextField label="Naam" value={data.presetName} name="presetName" labelWidth="5ch" />
			<ArrayField
				value={data.rounds}
				label="Rondes"
				labelSingle="ronde"
				entryBuilder={(v, i) => (
					<div class="flex flex-col gap-1">
						<h3 class="font-bold text-gray-800">Ronde {i + 1}</h3>
						<div class="flex flex-row gap-4">
							<div class="flex flex-1 flex-col gap-3">
								<TextField label="Onderwerp" value={v?.topic} name={`rounds[${i}][topic]`} labelWidth="10ch" />
								<DropdownField
									label="Correct Antwoord"
									value={v?.correctAnswer}
									name={`rounds[${i}][correctAnswer]`}
									options={ZintuigenEmotions.map((e) => ({
										label: `${toTitleCase(e)} (${toTitleCase(medalForEmotions(e))})`,
										value: e,
									}))}
									labelWidth="14ch"
								/>
							</div>
							<FileField label="Round image" name={`rounds[${i}][roundImage]`} file={v?.roundImage} />
						</div>
						<div class="flex flex-row gap-4">
							<FileField label="Foto 1" name={`rounds[${i}][photo1]`} file={v?.photo1} />
							<FileField label="Foto 2" name={`rounds[${i}][photo2]`} file={v?.photo2} />
							<FileField label="Foto 3" name={`rounds[${i}][photo3]`} file={v?.photo3} />
						</div>
					</div>
				)}
			/>

			<div submit-btn-area>
				<div class="flex justify-between">
					<Button type="button" mode="secondary" on:click={onCancel}>
						Annuleren
					</Button>
					<Button submit-btn>Opslaan</Button>
				</div>
				<div form-validation-errors-area class="text-danger-800"></div>
			</div>
		</Form>
	);
}

const pileLabels: Record<'goodCards' | 'badCards' | 'desireCards', string> = {
	goodCards: 'Positieve kaarten',
	badCards: 'Negatieve kaarten',
	desireCards: 'Verlangen kaarten',
};

function CardListEditor({
	pathPrefix,
	defaults,
	initial,
}: {
	pathPrefix: string;
	defaults: JackpotCard[];
	initial: JackpotCard[];
}) {
	const list: HTMLElement = (<div class="flex flex-col gap-1"></div>) as HTMLElement;

	function addRow(card: JackpotCard) {
		const row: HTMLElement = (
			<div class="flex items-center gap-2">
				<input
					type="text"
					name={`${pathPrefix}[-1][name]`}
					value={card.name}
					required
					class="min-w-0 flex-1 rounded-md border-0 px-2 py-1 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-theme-600 dark:bg-gray-900 dark:text-gray-100 dark:ring-gray-700"
				/>
				<IconPicker name={`${pathPrefix}[-1][icon]`} value={card.icon} />
				<Button
					type="button"
					mode="tertiary"
					size="sm"
					on:click={() => {
						row.remove();
					}}
				>
					<Icon icon={Trash} class="size-4" />
				</Button>
			</div>
		) as HTMLElement;
		list.appendChild(row);
	}

	for (const c of initial) addRow(c);

	return (
		<div class="flex flex-col gap-2">
			{list}
			<div class="flex gap-2">
				<Button
					type="button"
					mode="secondary"
					size="sm"
					on:click={() => {
						addRow({ name: '' });
					}}
				>
					Voeg woord toe
				</Button>
				<Button
					type="button"
					mode="tertiary"
					size="sm"
					on:click={() => {
						if (!window.confirm('Weet je zeker dat je deze lijst wilt vervangen door de standaard woorden?')) return;
						list.innerHTML = '';
						for (const c of defaults) addRow(c);
					}}
				>
					Reset naar standaard
				</Button>
			</div>
		</div>
	);
}

function RoundEditor({ round, initial }: { round: 1 | 2 | 3; initial: JackpotPreset['round1'] }) {
	const key = `round${round}` as const;
	const defaults = defaultRoundWordData[round];
	const piles = ['goodCards', 'badCards', 'desireCards'] as const;
	return (
		<details class="rounded border border-gray-200 dark:border-gray-700">
			<summary class="cursor-pointer select-none bg-gray-50 p-2 text-sm font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-200">
				Ronde {round} — woorden
			</summary>
			<div class="flex flex-col gap-4 p-3">
				{piles.map((pile) => {
					const initialPile = initial?.[pile] ?? defaults[pile];
					return (
						<details class="rounded border border-gray-200 dark:border-gray-700">
							<summary class="cursor-pointer select-none bg-gray-50 px-2 py-1 text-sm font-medium text-gray-700 dark:bg-gray-900 dark:text-gray-300">
								{pileLabels[pile]} ({initialPile.length})
							</summary>
							<div class="p-2">
								<CardListEditor pathPrefix={`${key}[${pile}]`} defaults={defaults[pile]} initial={initialPile} />
							</div>
						</details>
					);
				})}
			</div>
		</details>
	);
}

function JackpotPresetForm({
	data,
	onSubmit,
	onCancel,
}: {
	data?: JackpotPreset;
	onSubmit: (d: JackpotPreset) => void;
	onCancel: () => void;
}) {
	if (!data) data = JackpotPresetSchema.partial().parse({}) as any;
	return (
		<Form schema={JackpotPresetSchema} onSubmit={onSubmit}>
			<TextField label="Naam" value={data?.presetName} name="presetName" labelWidth="5ch" />

			<hr />
			<TextField label="Ronde 1 titel" value={data?.round1Title} name="round1Title" labelWidth="10ch" />
			<RoundEditor round={1} initial={data?.round1} />

			<hr />
			<TextField label="Ronde 2 titel" value={data?.round2Title} name="round2Title" labelWidth="10ch" />
			<RoundEditor round={2} initial={data?.round2} />

			<hr />
			<TextField label="Ronde 3 titel" value={data?.round3Title} name="round3Title" labelWidth="10ch" />
			<RoundEditor round={3} initial={data?.round3} />

			<div submit-btn-area>
				<div class="flex justify-between">
					<Button type="button" mode="secondary" on:click={onCancel}>
						Annuleren
					</Button>
					<Button submit-btn>Opslaan</Button>
				</div>
				<div form-validation-errors-area class="text-danger-800"></div>
			</div>
		</Form>
	);
}

function VingerafdrukkenPresetForm({
	data,
	onSubmit,
	onCancel,
}: {
	data?: VingerAfdrukkenPreset;
	onSubmit: (d: VingerAfdrukkenPreset) => void;
	onCancel: () => void;
}) {
	if (!data) data = VingerAfdrukkenPresetSchema.parse({}) as any;

	const zones: { zone: VingerAfdrukZone; themeLabel: string; fieldName: string }[] = [
		{ zone: 'I', themeLabel: 'Thema 1', fieldName: 'theme1[name]' },
		{ zone: 'M', themeLabel: 'Thema 2', fieldName: 'theme2[name]' },
		{ zone: 'P', themeLabel: 'Thema 3', fieldName: 'theme3[name]' },
		{ zone: 'V', themeLabel: 'Thema 4', fieldName: 'theme4[name]' },
	];
	const themeValues = [data?.theme1.name, data?.theme2.name, data?.theme3.name, data?.theme4.name];
	const zoneByNumber = Object.fromEntries(defaultQuestionsData.map((q) => [q.number, q.zone]));

	function handleSubmit(d: VingerAfdrukkenPreset) {
		const prefixed: Record<string, string> = {};
		for (const [key, text] of Object.entries(d.customQuestions ?? {})) {
			if (!text) continue;
			const num = parseInt(key.replace('q', ''));
			const zone = zoneByNumber[num];
			prefixed[key] = text.startsWith(`$${zone}: `) ? text : `$${zone}: ${text}`;
		}
		onSubmit({ ...d, customQuestions: prefixed });
	}

	return (
		<Form schema={VingerAfdrukkenPresetSchema} onSubmit={handleSubmit}>
			<TextField label="Naam" value={data?.presetName} name="presetName" labelWidth="5ch" />

			<hr />
			{zones.map(({ themeLabel, fieldName }, i) => (
				<TextField compact label={themeLabel} value={themeValues[i]} name={fieldName} labelWidth="10ch" />
			))}

			<hr />
			<p class="text-sm text-gray-600">Laat een veld leeg om de standaard vraagtekst te gebruiken.</p>
			{zones.map(({ zone, themeLabel }) => {
				const zoneQuestions = defaultQuestionsData.filter((q) => q.zone === zone);
				return (
					<details class="rounded border border-gray-200 dark:border-gray-700">
						<summary class="cursor-pointer select-none bg-gray-50 p-2 text-sm font-medium text-gray-800 dark:bg-gray-900 dark:text-gray-200">
							{themeLabel} — vragen ({zoneQuestions.length})
						</summary>
						<p class="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900">
							Gebruik <code class="rounded bg-gray-200 px-1 font-mono dark:bg-gray-700">${zone}</code> om de naam van{' '}
							{themeLabel} in te voegen.
						</p>
						<div class="flex flex-col gap-3 p-3">
							{zoneQuestions.map((q) => (
								<TextField
									optional
									compact
									label={`Vraag ${q.number}`}
									value={(data?.customQuestions?.[`q${q.number}`] ?? '').replace(/^\$[IMPV]: /, '')}
									name={`customQuestions[q${q.number}]`}
									placeholder={q.text.replace(/^\$[IMPV]: /, '')}
									labelWidth="9ch"
								/>
							))}
						</div>
					</details>
				);
			})}

			<div submit-btn-area>
				<div class="flex justify-between">
					<Button type="button" mode="secondary" on:click={onCancel}>
						Annuleren
					</Button>
					<Button submit-btn>Opslaan</Button>
				</div>
				<div form-validation-errors-area class="text-danger-800"></div>
			</div>
		</Form>
	);
}

function BouwHetLandschapSpelPresetForm({
	data,
	onSubmit,
	onCancel,
}: {
	data?: BouwHetLandschapPreset;
	onSubmit: (d: BouwHetLandschapPreset) => void;
	onCancel: () => void;
}) {
	if (!data) data = BouwHetLandschapPresetSchema.parse({}) as any;
	return (
		<Form schema={BouwHetLandschapPresetSchema} onSubmit={onSubmit}>
			<TextField label="Naam" value={data?.presetName} name="presetName" labelWidth="5ch" />

			<hr />
			<TextField label="Thema 1" value={data?.theme1.name} name="theme1[name]" labelWidth="10ch" />
			<TextField label="Thema 2" value={data?.theme2.name} name="theme2[name]" labelWidth="10ch" />
			<TextField label="Thema 3" value={data?.theme3.name} name="theme3[name]" labelWidth="10ch" />
			<TextField label="Thema 4" value={data?.theme4.name} name="theme4[name]" labelWidth="10ch" />

			<div submit-btn-area>
				<div class="flex justify-between">
					<Button type="button" mode="secondary" on:click={onCancel}>
						Annuleren
					</Button>
					<Button submit-btn>Opslaan</Button>
				</div>
				<div form-validation-errors-area class="text-danger-800"></div>
			</div>
		</Form>
	);
}

function PlayAndConnectSpelPresetForm({
	data,
	onSubmit,
	onCancel,
}: {
	data?: PlayAndConnectPreset;
	onSubmit: (d: PlayAndConnectPreset) => void;
	onCancel: () => void;
}) {
	if (!data) data = PlayAndConnectPresetSchema.parse({}) as any;
	return (
		<Form schema={PlayAndConnectPresetSchema} onSubmit={onSubmit}>
			<TextField label="Naam" value={data?.presetName} name="presetName" labelWidth="5ch" />

			<hr />
			<ArrayField
				value={data?.rounds ?? []}
				label="Vragen"
				labelSingle="vraag"
				entryBuilder={(v, i) => (
					<div class="flex flex-col gap-1">
						<h3 class="font-bold text-gray-800">Vraag {i + 1}</h3>
						<div class="flex flex-row items-start gap-4">
							<div class="flex-1">
								<TextField label="Vraag" value={v?.question} name={`rounds[${i}][question]`} />
							</div>
							<div class="w-24">
								<TextField
									label="Rondes"
									value={v?.rounds?.toString() ?? '1'}
									name={`rounds[${i}][rounds]`}
									type="number"
									min="1"
									max="99"
								/>
								<small class="text-xs text-gray-600">aantal rondes</small>
							</div>
						</div>
					</div>
				)}
			/>

			<div submit-btn-area>
				<div class="flex justify-between">
					<Button type="button" mode="secondary" on:click={onCancel}>
						Annuleren
					</Button>
					<Button submit-btn>Opslaan</Button>
				</div>
				<div form-validation-errors-area class="text-danger-800"></div>
			</div>
		</Form>
	);
}

function PresetTable({ license, cols }: { license: GameLicense; cols: Record<string, (d: any) => JSXChild> }) {
	return (
		<div class="mt-4">
			<Table>
				<thead>
					<TrHead>
						<Th>Name</Th>
						{Object.keys(cols).map((k) => (
							<Th>{k}</Th>
						))}
						<Th>Edit</Th>
						<Th>Delete</Th>
					</TrHead>
				</thead>
				<tbody>
					{license.presets.map((game: ZintuigenPreset, i) => (
						<TrBody class="">
							<Td>{game.presetName}</Td>
							{Object.values(cols).map((f) => (
								<Td>{f(game)}</Td>
							))}
							<Td>
								<Button
									size="sm"
									mode="tertiary"
									on:click={() => {
										presetEditModal(license, i);
									}}
								>
									Edit
								</Button>
							</Td>
							<Td>
								<Button
									size="sm"
									mode="tertiary"
									on:click={async () => {
										// delete this preset
										license.presets.splice(i, 1);
										const _res = await rpc.updateGameLicense.query({
											_id: license._id!,
											presets: license.presets,
										});
										showToast(<Toast icon={Trash} title="Preset verwijderd" />, 3000);
										reloadCurrentView();
									}}
								>
									Delete
								</Button>
							</Td>
						</TrBody>
					))}
				</tbody>
			</Table>
		</div>
	);
}

function presetEditModal(license: GameLicense, index: number = -1) {
	const preset = license.presets ? license.presets[index] : undefined;

	const submitHandler = async (confirm: () => void, d: any) => {
		// console.log('updating...');
		// console.log(d);
		confirm();
		if (!license.presets) license.presets = [];
		if (index === -1) {
			license.presets.push(d);
		} else {
			license.presets[index] = d;
		}

		const _res = await rpc.updateGameLicense.query({
			_id: license._id!,
			presets: license.presets,
		});
		// console.log('got res', res);
		showToast(<Toast icon={CheckCheck} title="Preset aangemaakt!" />, 3000);
		// console.log(d);
		reloadCurrentView();
	};

	return showModal((confirm, cancel) => (
		<Modal title="Nieuwe preset" confirm={null} cancel={null}>
			<div class="-mx-4 max-h-[80vh] min-w-[60vw] overflow-auto px-4">
				{license.kind === 'facilitaire-zintuigen-spel' && (
					<FacilitaireZintuigenPresetForm data={preset} onSubmit={(d) => submitHandler(confirm, d)} onCancel={cancel} />
				)}
				{license.kind === 'jackpot-van-las-vegas' && (
					<JackpotPresetForm data={preset} onSubmit={(d) => submitHandler(confirm, d)} onCancel={cancel} />
				)}
				{license.kind === 'vinger-afdrukken-spel' && (
					<VingerafdrukkenPresetForm data={preset} onSubmit={(d) => submitHandler(confirm, d)} onCancel={cancel} />
				)}
				{license.kind === 'bouw-het-landschap-spel' && (
					<BouwHetLandschapSpelPresetForm data={preset} onSubmit={(d) => submitHandler(confirm, d)} onCancel={cancel} />
				)}
				{license.kind === 'play-and-connect-spel' && (
					<PlayAndConnectSpelPresetForm data={preset} onSubmit={(d) => submitHandler(confirm, d)} onCancel={cancel} />
				)}
			</div>
		</Modal>
	));
}
